import React, { useState, useEffect } from 'react';
import { Button } from 'aha-components';
import { isMobile } from '@/biz/tool';
import WebModal from './Modal';
import BottomDrawer from './BottomDrawer';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import ModalAnalysisBreadcrumb from './ModalAnalysisBreadcrumb';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { WarnIcon } from '@/components/common/icon';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import { NumericFormat } from 'react-number-format';
import PriceFieldsComponent from './PriceFieldsComponent';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { OrderStatus } from '@/interface/order';
import axiosInstance from '../../../apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { BasePriceKeyListMap } from '@/interface/common';

interface NumberFormatUSDProps {
  inputRef: (instance: any) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

function NumberFormatUSD(props: NumberFormatUSDProps) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        });
      }}
      thousandSeparator
      valueIsNumericString
      allowNegative={false}
      // suffix=" USD"
    />
  );
}

const StyledTextField = withStyles({
  root: {
    '& .MuiInputBase-root': {
      border: '1px solid #E8E8EC',
      backgroundColor: '#ffffff',
      marginTop: '8px',
      marginBottom: '8px',
      '&:before': {
        borderBottom: '1px solid #E8E8EC' // 默认状态的底线颜色
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: '2px solid #5B3DE9' // hover 状态的底线颜色
      },
      '&.Mui-focused:after': {
        borderBottom: '2px solid #5B3DE9' // 输入态（focused）的底线颜色
      },
      '&.Mui-disabled': {
        '&:before': {
          borderBottomStyle: 'solid'
        }
      },
      borderRadius: '6px',
      '& input': {
        padding: '8px',
        caretColor: '#295eff', // 设置输入光标为紫色'
        '&::placeholder': {
          color: '#8B8D98',
          opacity: 1,
          fontSize: '14px',
          fontWeight: 300,
          fontFamily: 'Inter'
        }
      }
    }
  }
})(TextField);

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const { showModalConfirmDealPrice, setShowModalConfirmDealPrice } =
    useWebModalStore();
  const { detailData } = useDetailDataStore();

  if (!detailData?.order?.status.includes(OrderStatus.Invited)) {
    return null;
  }

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          title={t('modal.confirm_deal_price.title', {
            defaultValue: 'Confirm deal price'
          })}
          visible={showModalConfirmDealPrice}
          handleClose={() => setShowModalConfirmDealPrice(false)}
          isShowFooter={false}
          rootClassName="!z-[9999]"
        >
          <AnalysisContent />
        </BottomDrawer>
      ) : (
        <WebModal
          width={810}
          title={t('modal.confirm_deal_price.title', {
            defaultValue: 'Confirm deal price'
          })}
          visible={showModalConfirmDealPrice}
          handleClose={() => setShowModalConfirmDealPrice(false)}
          isShowFooter={false}
        >
          <AnalysisContent />
        </WebModal>
      )}
    </>
  );
};

const AnalysisContent: React.FC = () => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const {
    showModalConfirmDealPrice,
    setShowModalConfirmDealPrice,
    setShowModalMissOut,
    setShowModalSubmitInterest
  } = useWebModalStore();
  const { selectedPriceFields, setSelectedPriceFields, setRateNonNegotiable } =
    useMultiplePricesStore();

  const needUpdate = detailData?.order?.validateResult?.need_update;
  const updateReason = detailData?.order?.validateResult?.update_reason;
  const [newOrderPrice, setNewOrderPrice] = useState<number | string>('');
  const [nextLoading, setNextLoading] = useState(false);
  const [maxOrderPrice, setMaxOrderPrice] = useState<number | null>(null);
  const [nonNegotiable, setNonNegotiable] = useState(false);
  const { user } = usePlatformStore();
  const nextClick = async () => {
    if (newOrderPrice && +newOrderPrice > 0 && selectedPriceFields.length > 0) {
      const params = {
        interestedPriceField: {
          ...selectedPriceFields[0],
          actualGet: +newOrderPrice
        }
      };
      setNextLoading(true);
      try {
        const { data } = await axiosInstance.post(
          `/order/${detailData?.order?.id}/competitivePrice`,
          params
        );
        if (
          data?.data?.interestedPriceField.creatorMaxActualGet >= +newOrderPrice
        ) {
          setNextLoading(false);
          setSelectedPriceFields([data?.data?.interestedPriceField]);
          setRateNonNegotiable(nonNegotiable);
          setShowModalConfirmDealPrice(false);
          setShowModalSubmitInterest(true);
        } else {
          setMaxOrderPrice(
            data?.data?.interestedPriceField.creatorMaxActualGet
          );
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        setNextLoading(false);
      }
    } else {
      setShowModalConfirmDealPrice(false);
      setShowModalSubmitInterest(true);
    }
  };

  // 没有价格的时候，设置价格默认为第一种平台类型设置的价格
  useEffect(() => {
    if (!newOrderPrice) {
      const { priceFields, platform } = detailData?.order;
      const basePriceKey =
        BasePriceKeyListMap[platform]?.[priceFields?.[0]?.contentType];
      const setedBasePrice = user?.baselineConfig?.[basePriceKey || ''] || '';
      setNewOrderPrice(setedBasePrice);
    }
  }, [detailData?.order]);

  return (
    <div
      className={`w-full flex flex-col items-start gap-6
      ${isMobile() ? 'h-[calc(100vh)]' : ''}
    `}
    >
      <ModalAnalysisBreadcrumb index={1} />
      <div
        className={`w-full overflow-auto flex flex-col gap-6
          ${isMobile() ? 'h-[calc(100vh-210px)]  pb-10' : 'max-h-[calc(100vh-400px)]'}`}
      >
        {needUpdate && (
          <div className="w-full p-2 bg-[#ffeddc] rounded-lg justify-start inline-flex items-center gap-2">
            <WarnIcon className="w-6 " />
            <div className="grow shrink basis-0 text-[#1c2024] text-xs font-medium font-['Inter'] leading-[18px]">
              {updateReason?.includes('mismatch_audience')
                ? t('modal.confirm_deal_price.audience_mismatch_message', {
                    defaultValue:
                      'Based on how well your audience aligns with our brand requirements, '
                  })
                : null}
              {updateReason?.includes('content_check_failed')
                ? t('modal.confirm_deal_price.content_check_message', {
                    defaultValue: 'Based on your actual organic view data, '
                  })
                : null}
              {t('modal.confirm_deal_price.price_adjustment_message', {
                defaultValue:
                  'we have adjusted the pricing for your deal accordingly. Please review the updated price details below.'
              })}
            </div>
          </div>
        )}
        <div className="text-[#1c2024] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
          {t('modal.confirm_deal_price.select_content_type', {
            defaultValue: 'Select content type'
          })}{' '}
        </div>
        <PriceFieldsComponent
          direction={isMobile() ? 'flex flex-col' : 'flex flex-row'}
          hasRadioButton={true}
          orderPriceAdjustment={true}
          newOrderPrice={newOrderPrice}
          setNewOrderPrice={setNewOrderPrice}
          maxOrderPrice={maxOrderPrice as number | null}
          nonNegotiable={nonNegotiable}
          setNonNegotiable={setNonNegotiable as (v: boolean) => void}
        />
      </div>
      {/*  */}
      <div
        className={`bg-white w-full flex justify-end items-center gap-4 border-t border-solid border-[#e8e8ec] pt-4 mt-4
            ${isMobile() ? 'justify-center fixed bottom-0 left-0 pb-4' : ''}
          `}
      >
        <Button
          className="!w-[160px] !h-10"
          type="default"
          onClick={() => {
            setShowModalConfirmDealPrice(false);
            setShowModalMissOut(true);
          }}
        >
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          className="!w-[160px] !h-10"
          type="primary"
          disabled={nextLoading || !newOrderPrice}
          onClick={nextClick}
          loading={nextLoading}
        >
          {t('common.next', { defaultValue: 'Next' })}
        </Button>
      </div>
    </div>
  );
};

export default ModalShowAnInterest;
